import { useId } from "react";
import { Sheet } from "../ui/Sheet";
import { Pill } from "../ui/Pill";
import { FieldCard } from "../fields/FieldCard";
import { CrisisResources } from "../safety/CrisisResources";
import { familyWellnessTemplate } from "../../templates/familyWellness";
import type { FieldState, FollowupState, SafetyState, VerbatimEntry } from "../../engine/types";

/**
 * A read-only view of one family/household row, whether it comes from the
 * illustrative sample roster or from the live in-memory interview state.
 * Both sources are shaped as `FieldState`/`SafetyState`/`FollowupState`
 * (engine/types.ts), so this component doesn't need to know which one it's
 * looking at — except for `isLive`, which drives the badge that tells the
 * viewer which kind of data they're seeing (§21 item 12: never let the two
 * be confused).
 */
export type FamilyDrawerData = {
  displayName: string;
  isLive: boolean;
  fields: Record<string, FieldState>;
  safety: SafetyState;
  followup: FollowupState;
};

const NOTES_FIELD_ID = "notes_verbatim";
const ASKABLE_FIELDS = familyWellnessTemplate.fields.filter((f) => f.askable);

function verbatimEntries(fields: Record<string, FieldState>): VerbatimEntry[] {
  const value = fields[NOTES_FIELD_ID]?.value;
  return Array.isArray(value) && value.length > 0 && typeof value[0] === "object" && "text" in (value[0] as object)
    ? (value as VerbatimEntry[])
    : [];
}

export function FamilyDrawer({
  open,
  onClose,
  data,
}: {
  open: boolean;
  onClose: () => void;
  data: FamilyDrawerData | null;
}) {
  const headingId = useId();

  if (!open || !data) return null;

  const notes = verbatimEntries(data.fields);
  const isPaused = data.safety.status === "paused";

  return (
    <Sheet
      open={open}
      onClose={onClose}
      aria-labelledby={headingId}
      className="fixed inset-y-0 right-0 z-30 w-full max-w-md space-y-4 overflow-y-auto border-l border-border bg-paper p-4 shadow-xl"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 id={headingId} className="font-display text-lg text-ink">
            {data.displayName}
          </h2>
          <div className="mt-1">
            {data.isLive ? (
              <Pill tone="clay">Current session — this device</Pill>
            ) : (
              <Pill tone="neutral">Illustrative sample data</Pill>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-2 py-1 text-sm text-ink-soft hover:bg-paper-dim hover:text-ink"
          aria-label="Close"
        >
          Close
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={isPaused ? "danger" : data.followup.needed ? "warn" : "sage"}>
          {isPaused ? "Safety flagged" : data.followup.needed ? "Needs follow-up" : "No follow-up flagged"}
        </Pill>
      </div>

      {isPaused && (
        <div className="space-y-2 rounded-lg border border-danger/30 bg-clay-dim p-3">
          <p className="text-sm font-medium text-ink">{data.safety.message}</p>
          <CrisisResources resources={data.safety.resources ?? []} />
        </div>
      )}

      {data.followup.reasons.length > 0 && (
        <div className="text-xs text-ink-soft">
          Follow-up reasons: {data.followup.reasons.join(", ")}
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-ink-soft">Structured fields</h3>
        <div className="space-y-2">
          {ASKABLE_FIELDS.map((field) => (
            <FieldCard key={field.id} field={field} state={data.fields[field.id]} />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-ink-soft">Verbatim notes</h3>
        {notes.length === 0 ? (
          <p className="text-sm text-ink-soft">No verbatim notes recorded.</p>
        ) : (
          <ul className="space-y-2">
            {notes.map((entry, i) => (
              <li key={i} className="rounded-lg border border-border bg-paper-dim p-2 text-sm italic text-ink">
                "{entry.text}"
                <span className="ml-2 not-italic text-xs text-ink-soft">
                  ({entry.lang}, {entry.inputMode})
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}
